import React, { useCallback, useEffect, useState } from "react";
import { getAccessPermission } from "../../../reducers/PermissionsReducer";
import { useDispatch, useSelector } from "react-redux";
import {
	createEquipment,
	deleteEquipment,
	getEquipment,
	getEquipmentById,
	updateEquipment,
} from "../../../reducers/EquipmentsReducer";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	TableCell,
	TableRow,
} from "@mui/material";
import { ExpandMore, HomeRepairService } from "@mui/icons-material";
import PATAddButton from "../../../components/Buttons/PATAddButton";
import PATDeleteButton from "../../../components/Buttons/PATDeleteButton";
import PATEditButton from "../../../components/Buttons/PATEditButton";
import CustomTable from "../../../components/CustomTable";
import {
	capitalizeFirstLetter,
	daysBetween,
	getCodeDescription,
	getCodeId,
	mapDictionaryOptions,
	StatusBadge,
} from "../../../utils/functions";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";
import { notify } from "../../../reducers/NotifierReducer";
import AddEditEquipmentDialog from "../../Equipments/AddEditEquipmentDialog";
import { getEmployees, getFundingSources } from "../../../utils/commonServices";
import { getVehicleOptions } from "../../WorkOrders/useWorkOrdersData";

const tableHeaders = [
	"Type",
	"Property#",
	"Serial#",
	"Brand / Model",
	"Condition",
	"Status",
	"Location",
	"Vehicle",
	"Service Due",
	"Actions",
];

const EquipmentsTable = (props) => {
	const { allExpanded, perPersonnelId, theme, readOnly } = props;

	const dispatch = useDispatch();
	const userAgency = useSelector((state) => state.user.userAgency);
	const dictionary = useSelector((state) => state.dictionary.dictionary);
	const { userData } = useSelector((state) => state.user);

	const [expanded, setExpanded] = useState(allExpanded);
	const [selectedId, setSelectedId] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedEquipment, setSelectedEquipment] = useState(null);
	const [vehicles, setVehicles] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [fundingSources, setFundingSources] = useState([]);
	const [isLoadData, setIsLoadData] = useState(false);
	const [isinitialLoadData, setIsinitialLoadData] = useState(false);

	const { equipment } = useSelector(
		(state) =>
			state?.equipment || {
				equipment: [],
				loading: false,
				total: 0,
			},
	);

	const equipmentPermission = getAccessPermission(
		"employees",
		"Equipments",
		"any",
	);
	const canAddEquipment = equipmentPermission?.Create ?? true;
	const canEditEquipment = equipmentPermission?.Edit ?? true;
	const canDeleteEquipment = equipmentPermission?.Delete ?? true;

	const typeOptions =
		mapDictionaryOptions(dictionary.codePerEquipmentTypes) || [];
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const statusOptions =
		mapDictionaryOptions(dictionary.codePerEquipmentStatus) || [];
	const conditionOptions =
		mapDictionaryOptions(dictionary.codePerEquipmentConditions) || [];
	const categoryOptions =
		mapDictionaryOptions(dictionary.codePerEquipmentCategory) || [];
	const divisionOptions =
		mapDictionaryOptions(dictionary.codePerDepartment) || [];
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const serviceRequirements =
		mapDictionaryOptions(dictionary.codePerEquipmentServiceRequirements) ||
		[];

	useEffect(() => {
		setExpanded(allExpanded);
	}, [allExpanded]);

	const handleLoadData = async () => {
		const [empList, vehicleList, fundingList] = await Promise.all([
			getEmployees(userAgency, true),
			getVehicleOptions(userAgency),
			getFundingSources(userAgency, true),
		]);
		setEmployees(empList);
		setVehicles(vehicleList);
		setFundingSources(fundingList);
		setIsLoadData(true);
	};

	useEffect(() => {
		if (userAgency && dictionary && !isLoadData && (openDialog || expanded)) {
			handleLoadData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency, isLoadData, dictionary, openDialog, expanded]);

	const getEquipmentList = () => {
		dispatch(
			getEquipment(
				0,
				-1,
				"id",
				"asc",
				"",
				{ perPersonnelId: [{ value: perPersonnelId }] },
				userAgency,
			),
		);
	};

	useEffect(() => {
		if (perPersonnelId && !isinitialLoadData && expanded && (!equipment || equipment?.length === 0)) {
			getEquipmentList();
			setIsinitialLoadData(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [perPersonnelId, equipment, expanded, dispatch]);

	const getVehicleDisplay = useCallback(
		(vehicleId) => {
			const vehicle = vehicles.find((v) => v.value === vehicleId);
			return vehicle ? vehicle.label : vehicleId || "—";
		},
		[vehicles],
	);

	// Handle delete
	const handleDelete = async () => {
		try {
			await dispatch(
				deleteEquipment(
					selectedId,
					userData.user.ptsUserID,
					selectedEquipment,
				),
			);
			setOpenDeleteDialog(false);
			setSelectedId(null);
			setSelectedEquipment(null);
			dispatch(notify("Equipment deleted successfully", "success"));
		} catch (error) {
			dispatch(notify("Failed to delete equipment", "error"));
		}
	};

	const handleSaveEquipment = async (payload) => {
		try {
			// Extract serviceRequirements from payload
			const { serviceRequirements: svcReqIds, ...equipmentData } =
				payload;

			const data = {
				...equipmentData,
				typeId: getCodeId(
					payload.type,
					dictionary?.codePerEquipmentTypes,
				),
				statusId: getCodeId(
					payload.status,
					dictionary?.codePerEquipmentStatus,
				),
				conditionId: getCodeId(
					payload.condition,
					dictionary?.codePerEquipmentConditions,
				),
				categoryCodeId: getCodeId(
					payload.categoryCode,
					dictionary?.codePerEquipmentCategory,
				),
				divisionId: getCodeId(
					payload.division,
					dictionary?.codePerDepartment,
				),
			};

			if (selectedEquipment) {
				await dispatch(
					updateEquipment(
						selectedEquipment.id,
						data,
						svcReqIds || [],
						userAgency,
						userData.user.ptsUserID,
						selectedEquipment,
					),
				);
			} else {
				await dispatch(
					createEquipment(
						data,
						svcReqIds || [],
						userAgency,
						userData.user.ptsUserID,
					),
				);
			}

			setOpenDialog(false);
			setSelectedEquipment(null);
			getEquipmentList();
		} catch (error) {
			dispatch(notify("Failed to save equipment", "error"));
		}
	};

	const refreshEquipmentData = useCallback(async () => {
		if (selectedEquipment?.id) {
			try {
				const response = await dispatch(
					getEquipmentById(selectedEquipment.id, userAgency),
				);
				// Check the actual response structure from your reducer
				const updatedEquipment = response?.payload || response;
				if (updatedEquipment) {
					setSelectedEquipment(updatedEquipment);
				}
			} catch (error) {
				console.error("Failed to refresh equipment:", error);
			}
		}
	}, [selectedEquipment?.id, userAgency, dispatch]);

	const getEqSvcStatus = (eq, req, maintenanceRecords) => {
		const DUE_SOON_PCT = 0.1;

		const done = (maintenanceRecords || [])
			.filter(
				(m) =>
					m.equipmentId === eq.id &&
					m.status === "Completed" &&
					m.date &&
					m.serviceRequirementId === req.ptsCodeId,
			)
			.sort((a, b) => new Date(b.date) - new Date(a.date));

		const lastDate = done[0]?.date || eq.acquiredDate || null;
		const daysSince = lastDate ? daysBetween(lastDate) : 9999;
		const intervalDays = req.intervalDays || Number(req.Value) || 365;
		const remaining = intervalDays - daysSince;
		const dueSoon = remaining <= intervalDays * DUE_SOON_PCT;

		return {
			status: remaining <= 0 ? "Overdue" : dueSoon ? "Due Soon" : "OK",
			dueIn: remaining,
			daysSince,
			lastServiceDate: lastDate,
		};
	};

	// Get worst service status for equipment
	const getServiceStatus = useCallback(
		(equipment) => {
			const eqServiceReqs = equipment?.serviceRequirements || [];
			const maintenanceRecords = equipment?.maintenanceRecords || [];

			if (!eqServiceReqs.length || !serviceRequirements.length) {
				return { worstSvc: null, svcStatuses: [] };
			}

			const svcStatuses = eqServiceReqs
				.map((ptsCodeId) => {
					// Find the service requirement definition
					const sr = serviceRequirements.find(
						(r) =>
							r.ptsCodeId === ptsCodeId || r.value === ptsCodeId,
					);
					if (!sr) return null;

					const status = getEqSvcStatus(
						equipment,
						sr,
						maintenanceRecords,
					);
					return { sr, ...status };
				})
				.filter(Boolean);

			// Find the worst status (Overdue > Due Soon > OK, then by dueIn)
			const worstSvc = svcStatuses.reduce((worst, current) => {
				if (!worst) return current;

				const priorityMap = { Overdue: 3, "Due Soon": 2, OK: 1 };
				const currentPriority = priorityMap[current.status] || 0;
				const worstPriority = priorityMap[worst.status] || 0;

				if (currentPriority > worstPriority) return current;
				if (
					currentPriority === worstPriority &&
					current.dueIn < worst.dueIn
				)
					return current;

				return worst;
			}, null);

			return { worstSvc, svcStatuses };
		},
		[serviceRequirements],
	);

	const renderService = (row) => {
		const { worstSvc, svcStatuses } = getServiceStatus(row);

		if (worstSvc) {
			const isOverdue = worstSvc.status === "Overdue";
			const isDueSoon = worstSvc.status === "Due Soon";
			const color = isOverdue
				? "#d32f2f"
				: isDueSoon
					? "#ed6c02"
					: "#2e7d32";

			return (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						lineHeight: 1.2,
					}}
				>
					<Box
						component="span"
						sx={{
							fontSize: 11,
							fontWeight: 700,
							color: color,
							whiteSpace: "nowrap",
						}}
					>
						{isOverdue
							? `${Math.abs(worstSvc.dueIn)}d overdue`
							: `${worstSvc.dueIn}d left`}
					</Box>
					<Box
						component="span"
						sx={{
							fontSize: 10,
							fontWeight: 400,
							color: "text.secondary",
						}}
					>
						{worstSvc.sr?.label || worstSvc.sr?.Code || ""}
					</Box>
				</Box>
			);
		}

		if (svcStatuses.length > 0) {
			return (
				<Box
					component="span"
					sx={{
						fontSize: 11,
						fontWeight: 600,
						color: "#2e7d32",
					}}
				>
					OK
				</Box>
			);
		}

		return (
			<Box
				component="span"
				sx={{
					fontSize: 11,
					color: "text.disabled",
				}}
			>
				—
			</Box>
		);
	};

	return (
		<>
			<Accordion
				expanded={expanded}
				onChange={() => setExpanded((prev) => !prev)}
				sx={{ mb: 1 }}
			>
				<AccordionSummary
					expandIcon={<ExpandMore />}
					sx={{
						backgroundColor: theme.accordion.backgroundColor,
						color: theme.accordion.textColor,
					}}
				>
					<Box
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						width="100%"
					>
						<Box display="flex" alignItems="center">
							<HomeRepairService sx={{ mr: 1 }} />
							<span>Equipment</span>
						</Box>
						<Box sx={{ mr: 2 }}>
							{!readOnly && canAddEquipment && (
								<PATAddButton
									tooltipText="Add Equipment"
									onClick={(e) => {
										e.stopPropagation();
										setOpenDialog(true);
									}}
								/>
							)}
						</Box>
					</Box>
				</AccordionSummary>
				<AccordionDetails>
					{equipment?.length > 0 ? (
						<CustomTable
							columns={tableHeaders}
							data={equipment}
							renderRow={(item, index) => (
								<TableRow key={index}>
									<TableCell>
										<StatusBadge
											status={capitalizeFirstLetter(
												getCodeDescription(
													item?.type ||
														item?.typeInfo?.Code,
													dictionary.codePerEquipmentTypes,
												),
											)}
										/>
									</TableCell>
									<TableCell>
										{item?.propertyNumber || "-"}
									</TableCell>
									<TableCell>
										{item?.serialNumber || "-"}
									</TableCell>
									<TableCell>
										{`${item?.brand || ""} ${item?.model || ""}`.trim() ||
											"—"}
									</TableCell>
									<TableCell>
										<StatusBadge
											status={capitalizeFirstLetter(
												getCodeDescription(
													item?.condition ||
														item?.conditionInfo
															?.Code,
													dictionary.codePerEquipmentConditions,
												),
											)}
										/>
									</TableCell>
									<TableCell>
										<StatusBadge
											status={capitalizeFirstLetter(
												getCodeDescription(
													item?.status ||
														item?.statusInfo?.Code,
													dictionary.codePerEquipmentStatus,
												),
											)}
										/>
									</TableCell>
									<TableCell>
										{item?.location || "-"}
									</TableCell>
									<TableCell>
										{item?.vehicleId ? getVehicleDisplay(item?.vehicleId) : "-"}
									</TableCell>
									<TableCell>{renderService(item)}</TableCell>
									<TableCell>
										<Box display="flex" gap={1}>
											{!readOnly && canEditEquipment && (
												<PATEditButton
													tooltipText="Edit Equipment"
													onClick={() => {
														setSelectedEquipment(
															item,
														);
														setOpenDialog(true);
													}}
													size="20px"
												/>
											)}
											{!readOnly && canDeleteEquipment && (
												<PATDeleteButton
													tooltipText="Delete Equipment"
													onClick={() => {
														setSelectedId(item?.id);
														setSelectedEquipment(
															item,
														);
														setOpenDeleteDialog(
															true,
														);
													}}
													size="20px"
												/>
											)}
										</Box>
									</TableCell>
								</TableRow>
							)}
						/>
					) : (
						<span>No equipment found.</span>
					)}
				</AccordionDetails>
			</Accordion>

			<DeleteConfirmationDialog
				open={openDeleteDialog}
				onClose={() => {
					setOpenDeleteDialog(false);
					setSelectedEquipment(null);
					setSelectedId(null);
				}}
				onConfirm={handleDelete}
				title="Delete Equipment"
				message="Are you sure you want to delete this equipment? This will also delete all assignment history."
			/>

			<AddEditEquipmentDialog
				open={openDialog}
				onClose={() => {
					setOpenDialog(false);
					setSelectedEquipment(null);
				}}
				onSave={handleSaveEquipment}
				perPersonnelId={perPersonnelId}
				isLoadData={isLoadData}
				initialData={selectedEquipment}
				statusOptions={statusOptions}
				typeOptions={typeOptions}
				categoryCodeOptions={categoryOptions}
				conditionOptions={conditionOptions}
				divisionOptions={divisionOptions}
				employeeOptions={employees}
				vehicleOptions={vehicles}
				fundingSourceOptions={fundingSources}
				serviceRequirements={serviceRequirements}
				assignmentHistory={selectedEquipment?.assignmentHistory || []}
				maintenanceRecords={selectedEquipment?.maintenanceRecords || []}
				canManageMaintenance={true}
				onMaintenanceUpdate={refreshEquipmentData}
			/>
		</>
	);
};

export default EquipmentsTable;
